import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Device, Call } from "@twilio/voice-sdk";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

// ---------------------------------------------------------------------------
// v9: Allowlist removed — all active team_roster members get the softphone.
// The edge function (twilio-voice-token v9) also removed its server-side
// allowlist. Any authenticated, active team member can mint a voice token.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SoftphoneStatus =
  | "disabled" // user not in allowlist
  | "registering" // fetching token / setting up Device
  | "ready" // Device registered, idle
  | "connecting" // outbound call placed, awaiting ring
  | "ringing" // outbound ringing OR inbound invite present
  | "in-call" // call connected
  | "ending" // disconnect in flight
  | "error";

export interface CallContext {
  /** E.164 phone (outbound) or caller phone (inbound) */
  phoneNumber: string;
  /** Deal id if this is a deal-scoped call */
  dealId?: string | null;
  /** Display name for caller / callee */
  displayName?: string | null;
  /** Outbound = we initiated, inbound = SDK received an invite */
  direction: "outbound" | "inbound";
  /** Tag for non-deal calls (e.g. "echo_test") */
  tag?: string | null;
}

interface SoftphoneAPI {
  isEnabled: boolean;
  status: SoftphoneStatus;
  error: string | null;
  activeCall: CallContext | null;
  /** Seconds elapsed since the call connected (status = "in-call") */
  durationSec: number;
  muted: boolean;
  /** Display friendly identity (member name) */
  identity: string | null;

  /** Place an outbound call. Resolves once the call leaves the SDK. */
  call: (ctx: { phoneNumber: string; dealId?: string | null; displayName?: string | null; tag?: string | null }) => Promise<void>;
  /** Accept the currently-ringing inbound call. No-op if no inbound invite. */
  accept: () => void;
  /** Reject the currently-ringing inbound call without answering. */
  reject: () => void;
  hangup: () => void;
  toggleMute: () => void;
  /** Manually re-init Device (e.g. after permission grant) */
  reinit: () => Promise<void>;
}

const SoftphoneContext = createContext<SoftphoneAPI | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const HEARTBEAT_INTERVAL_MS = 30_000;

// Minimum gap between automatic re-registration attempts after the Device
// dropped to "disabled" (laptop sleep, network blip). See the auto-recovery
// effect below.
const AUTO_REINIT_COOLDOWN_MS = 30_000;

export function SoftphoneProvider({ children }: { children: React.ReactNode }) {
  const { member } = useAuth();
  const isEnabled = !!member;

  const deviceRef = useRef<Device | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeCallRef = useRef<Call | null>(null);

  const [status, setStatus] = useState<SoftphoneStatus>(isEnabled ? "registering" : "disabled");
  const [error, setError] = useState<string | null>(null);
  const [activeCall, setActiveCall] = useState<CallContext | null>(null);
  const [durationSec, setDurationSec] = useState(0);
  const [muted, setMuted] = useState(false);
  const [identity, setIdentity] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Heartbeat — keeps softphone_last_seen fresh while Device is registered
  // -------------------------------------------------------------------------

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    // Fire one immediately, then every 30s
    void supabase.rpc("softphone_heartbeat").then(() => {});
    heartbeatTimerRef.current = setInterval(() => {
      void supabase.rpc("softphone_heartbeat").then(() => {});
    }, HEARTBEAT_INTERVAL_MS);
  }, [stopHeartbeat]);

  // Timestamp of the last time the tab went hidden — the auto-recovery effect
  // uses the hidden→visible gap to decide whether coming back needs a full
  // rebuild (long sleep) vs. a cheap check (quick tab flick). See below.
  const hiddenSinceRef = useRef(0);

  // -------------------------------------------------------------------------
  // Duration timer — counts up while in-call
  // -------------------------------------------------------------------------

  const stopDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const startDurationTimer = useCallback(() => {
    stopDurationTimer();
    setDurationSec(0);
    const t0 = Date.now();
    durationTimerRef.current = setInterval(() => {
      setDurationSec(Math.floor((Date.now() - t0) / 1000));
    }, 500);
  }, [stopDurationTimer]);

  // -------------------------------------------------------------------------
  // Wire a Call object to React state
  // -------------------------------------------------------------------------

  const wireCall = useCallback((call: Call, ctx: CallContext) => {
    activeCallRef.current = call;
    setActiveCall(ctx);

    call.on("ringing", () => setStatus("ringing"));
    call.on("accept", () => {
      setStatus("in-call");
      startDurationTimer();
    });
    call.on("disconnect", () => {
      setStatus("ready");
      stopDurationTimer();
      activeCallRef.current = null;
      setActiveCall(null);
      setMuted(false);
      setDurationSec(0);
    });
    call.on("cancel", () => {
      setStatus("ready");
      stopDurationTimer();
      activeCallRef.current = null;
      setActiveCall(null);
    });
    call.on("error", (e: { message?: string }) => {
      console.error("Call error:", e);
      setError(e?.message ?? "Call error");
      setStatus("error");
      stopDurationTimer();
      activeCallRef.current = null;
      setActiveCall(null);
    });
  }, [startDurationTimer, stopDurationTimer]);

  // -------------------------------------------------------------------------
  // Init Device
  // -------------------------------------------------------------------------

  const initDeviceInner = useCallback(async (): Promise<Device | null> => {
    setError(null);
    setStatus("registering");

    try {
      // Explicitly get the session so we always send a valid access token.
      // supabase.functions.invoke() falls back to the publishable key if
      // getSession() returns null, which the edge function can't validate.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn("[Softphone] No active session — disabling gracefully.");
        setError(null);
        setStatus("disabled");
        return null;
      }

      const { data, error: fnErr } = await supabase.functions.invoke("twilio-voice-token", {
        body: {},
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (fnErr || data?.error) {
        const msg = fnErr?.message ?? data?.error ?? "Failed to get voice token";
        console.warn("[Softphone] Token fetch failed — disabling gracefully:", msg);
        setError(null);
        setStatus("disabled");
        return null;
      }
      setIdentity(data.member_name ?? data.identity ?? null);

      // If we already have a Device, just update the token
      if (deviceRef.current) {
        try {
          deviceRef.current.updateToken(data.token);
          setStatus("ready");
          return deviceRef.current;
        } catch {
          deviceRef.current.destroy();
          deviceRef.current = null;
        }
      }

      const dev = new Device(data.token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        closeProtection: true,
        logLevel: 1,
      });

      dev.on("error", (e: { code?: number; message?: string }) => {
        // 31005 = WebSocket closed (reconnecting), 31009 = transport not connected.
        // These are transient and the SDK auto-reconnects.
        if (e?.code === 31005 || e?.code === 31009) return;

        // 31000 = generic / authorization error — the Device can't register.
        // 31204 / 31205 = invalid access token / JWT.
        // Treat these as "softphone unavailable" rather than a hard error so the
        // rest of the portal keeps working. The softphone card will show Disabled.
        if (e?.code === 31000 || e?.code === 31204 || e?.code === 31205) {
          console.warn("Softphone unavailable (Twilio %d) — disabling gracefully.", e.code, e);
          setError(null);
          setStatus("disabled");
          try { dev.destroy(); } catch { /* swallow */ }
          deviceRef.current = null;
          stopHeartbeat();
          return;
        }

        // Only surface as a blocking "error" state if there's an active call.
        // Device-level errors while idle degrade silently to "disabled".
        if (activeCallRef.current) {
          console.error("[Softphone] Device error during call:", e);
          setError(e?.message ?? "Device error");
          setStatus("error");
        } else {
          console.warn("[Softphone] Device error while idle — disabling gracefully:", e);
          setError(null);
          setStatus("disabled");
          try { dev.destroy(); } catch { /* swallow */ }
          deviceRef.current = null;
          stopHeartbeat();
        }
      });

      dev.on("registered", () => {
        setStatus((s) => (s === "in-call" || s === "ringing" || s === "connecting" ? s : "ready"));
        startHeartbeat();
      });

      dev.on("unregistered", () => {
        stopHeartbeat();
      });

      dev.on("tokenWillExpire", async () => {
        try {
          const { data: { session: s } } = await supabase.auth.getSession();
          const { data: refresh } = await supabase.functions.invoke("twilio-voice-token", {
            body: {},
            ...(s?.access_token ? { headers: { Authorization: `Bearer ${s.access_token}` } } : {}),
          });
          if (refresh?.token) dev.updateToken(refresh.token);
        } catch (err) {
          console.warn("Token refresh failed:", err);
        }
      });

      // Inbound call invite — wire the call so the global call bar can render
      // an Accept / Reject control. The Twilio Voice SDK requires explicit
      // call.accept() to answer; that's bound to the green button in
      // SoftphoneCallBar via the public `accept()` API on this context.
      dev.on("incoming", (call: Call) => {
        const fromIdent = call.parameters.From ?? "";
        wireCall(call, {
          phoneNumber: fromIdent,
          direction: "inbound",
          displayName: null,
        });
        setStatus("ringing");
      });

      await dev.register();
      deviceRef.current = dev;
      return dev;
    } catch (err: unknown) {
      // If the Device couldn't register (e.g. bad token, network error, Twilio
      // 31000 auth failure) we fall to "disabled" — NOT "error". This ensures
      // every other page in the portal keeps working normally. The softphone
      // card will show "Disabled" and users can retry via reinit().
      const code = (err as { code?: number })?.code;
      if (code === 31000 || code === 31204 || code === 31205) {
        console.warn("Softphone init failed (Twilio %d) — disabling gracefully.", code, err);
        setError(null);
        setStatus("disabled");
      } else {
        console.error("initDevice failed:", err);
        setError(String(err));
        setStatus("disabled"); // was "error" — downgrade to avoid blocking UI
      }
      // Clean up the half-initialized Device if one exists
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* swallow */ }
        deviceRef.current = null;
      }
      stopHeartbeat();
      return null;
    }
  }, [startHeartbeat, stopHeartbeat, wireCall]);

  // Concurrent-init guard: a Call-button on-demand revival and the
  // auto-recovery listener below can both request an init in the same
  // instant. Two parallel inits would each create + register a Device and
  // the loser would leak (still registered → double inbound ringing), so
  // every caller shares one in-flight attempt. initDeviceInner never
  // rejects (it catches everything and returns null), so no unhandled
  // rejection can stick to the shared promise.
  const initInFlightRef = useRef<Promise<Device | null> | null>(null);
  const initDevice = useCallback((): Promise<Device | null> => {
    if (!initInFlightRef.current) {
      initInFlightRef.current = initDeviceInner().finally(() => {
        initInFlightRef.current = null;
      });
    }
    return initInFlightRef.current;
  }, [initDeviceInner]);

  // -------------------------------------------------------------------------
  // Boot when member is in allowlist
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!isEnabled) {
      setStatus("disabled");
      return;
    }
    void initDevice();

    return () => {
      stopHeartbeat();
      stopDurationTimer();
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* swallow */ }
        deviceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, member?.id]);

  // -------------------------------------------------------------------------
  // Auto-recovery — a laptop sleep or network drop kills the Twilio
  // WebSocket. The Aug 31 version only re-inited when status was literally
  // "disabled", but a laptop that sleeps PAST the 1h token TTL leaves the SDK
  // reporting "ready" on a DEAD socket: the transient close fires 31005 (which
  // we ignore, expecting the SDK to auto-reconnect), the reconnect fails on
  // the expired token, and it never cleanly flips to "disabled". So on wake it
  // LOOKED alive but wasn't, nothing rebuilt it, and Hennie's softphone that
  // worked one afternoon was dead the next morning (Sep 9, 2026 — proven from
  // softphone_last_seen/last_token telemetry: connected Sep 8 13:49 UTC, zero
  // contact Sep 9).
  //
  // Fix: stop trusting the SDK's self-reported health. When the tab comes back
  // from a real away-period (hidden >= STALE_HIDDEN_MS), HARD-reset — destroy
  // the Device and re-init with a fresh token, exactly what a page reload does
  // (and reload always registered cleanly). Quick tab flicks just do a cheap
  // health check. A 60s interval backstops the case where the Device dropped
  // to Unregistered while the tab stayed visible (no focus event fires).
  //
  // Safe by construction: never touches a live call (IN_CALL guard), never
  // more than once per AUTO_REINIT_COOLDOWN_MS, and registration alone never
  // prompts for the mic (only an actual dev.connect() does).
  // -------------------------------------------------------------------------

  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);
  const lastAutoReinitRef = useRef(0);

  useEffect(() => {
    if (!isEnabled) return;
    const STALE_HIDDEN_MS = 60_000;
    const IN_CALL = new Set<SoftphoneStatus>(["connecting", "ringing", "in-call", "ending"]);

    const hardReset = () => {
      // Rebuild from scratch. A stale socket can leave device.state ===
      // Registered while dead, so we don't trust it — destroy + fresh token.
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* swallow */ }
        deviceRef.current = null;
      }
      stopHeartbeat();
      void initDevice();
    };

    const recover = (forceHard: boolean) => {
      if (document.visibilityState === "hidden") return;
      if (IN_CALL.has(statusRef.current)) return; // never disturb a live call
      const now = Date.now();
      if (now - lastAutoReinitRef.current < AUTO_REINIT_COOLDOWN_MS) return;

      const registered =
        !!deviceRef.current && deviceRef.current.state === Device.State.Registered;
      const healthy = registered && statusRef.current !== "disabled" && statusRef.current !== "error";

      if (forceHard) {
        lastAutoReinitRef.current = now;
        hardReset();
      } else if (!healthy) {
        lastAutoReinitRef.current = now;
        if (registered) void initDevice(); // token-refresh path
        else hardReset();                  // gone — rebuild
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now();
        return;
      }
      const gap = Date.now() - (hiddenSinceRef.current || Date.now());
      recover(gap >= STALE_HIDDEN_MS);
    };
    const onFocus = () => recover(false);
    const onOnline = () => recover(true); // network re-established → rebuild transport
    const iv = setInterval(() => recover(false), 60_000);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
  }, [isEnabled, initDevice, stopHeartbeat]);

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  const call = useCallback(async (ctx: { phoneNumber: string; dealId?: string | null; displayName?: string | null; tag?: string | null }) => {
    if (!isEnabled) throw new Error("Softphone not enabled for this user");
    let dev = deviceRef.current;
    if (!dev || dev.state !== Device.State.Registered) {
      dev = await initDevice();
    }
    if (!dev) throw new Error(error ?? "Softphone unavailable");

    setStatus("connecting");
    setError(null);

    try {
      const params: Record<string, string> = { To: ctx.phoneNumber };
      if (ctx.dealId) params.deal_id = ctx.dealId;
      if (ctx.tag) params.tag = ctx.tag;

      const newCall = await dev.connect({ params });
      wireCall(newCall, {
        phoneNumber: ctx.phoneNumber,
        dealId: ctx.dealId ?? null,
        displayName: ctx.displayName ?? null,
        direction: "outbound",
        tag: ctx.tag ?? null,
      });
    } catch (err) {
      console.error("call() failed:", err);
      setError(String(err));
      setStatus("error");
      throw err;
    }
  }, [error, initDevice, isEnabled, wireCall]);

  const hangup = useCallback(() => {
    const c = activeCallRef.current;
    if (!c) return;
    setStatus("ending");
    try { c.disconnect(); } catch { /* swallow */ }
  }, []);

  /** Accept a ringing inbound call. Twilio SDK requires explicit accept(). */
  const accept = useCallback(() => {
    const c = activeCallRef.current;
    if (!c) return;
    try {
      c.accept();
      // Status will flip to in-call via the wireCall "accept" listener.
    } catch (err) {
      console.error("Accept failed:", err);
      setError(String(err));
      setStatus("error");
    }
  }, []);

  /** Reject a ringing inbound call without answering. */
  const reject = useCallback(() => {
    const c = activeCallRef.current;
    if (!c) return;
    try {
      c.reject();
    } catch (err) {
      console.error("Reject failed:", err);
    }
    activeCallRef.current = null;
    setActiveCall(null);
    setStatus("ready");
  }, []);

  const toggleMute = useCallback(() => {
    const c = activeCallRef.current;
    if (!c) return;
    const next = !muted;
    try {
      c.mute(next);
      setMuted(next);
    } catch (err) {
      console.warn("Mute toggle failed:", err);
    }
  }, [muted]);

  const reinit = useCallback(async () => {
    await initDevice();
  }, [initDevice]);

  const value = useMemo<SoftphoneAPI>(() => ({
    isEnabled,
    status,
    error,
    activeCall,
    durationSec,
    muted,
    identity,
    call,
    accept,
    reject,
    hangup,
    toggleMute,
    reinit,
  }), [isEnabled, status, error, activeCall, durationSec, muted, identity, call, accept, reject, hangup, toggleMute, reinit]);

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
    </SoftphoneContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSoftphone(): SoftphoneAPI {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) {
    // Permissive default — components outside the provider just see "disabled"
    return {
      isEnabled: false,
      status: "disabled",
      error: null,
      activeCall: null,
      durationSec: 0,
      muted: false,
      identity: null,
      call: async () => { throw new Error("Softphone not mounted"); },
      accept: () => {},
      reject: () => {},
      hangup: () => {},
      toggleMute: () => {},
      reinit: async () => {},
    };
  }
  return ctx;
}

/** Format a duration in MM:SS for the call bar. */
export function formatCallDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
